<template>
  <div>
    <!-- Component view starts here -->
    <template v-if="componentView">
      <component :is="localComponent" />
    </template>
    <!-- Table View starts here -->
    <template v-else-if="tableView">
      <br v-if="!isPopup" />
      <v-card color="primary" elevation="0" class="white--text">
        <v-card-title>
          <h3>{{ localContent.title }}</h3>
        </v-card-title>
        <v-card-text class="white--text">
          <v-data-table
            :headers="localContent.headers"
            :items="localContent.items"
            :expanded.sync="expandedTableItem"
            :items-per-page="50"
            :mobile-breakpoint="1200"
            :hide-default-header="isPopup"
            :height="isPopup ? getHeight : null"
            hide-default-footer
            :single-expand="!docView"
            dark
            item-key="name"
            class="secondary"
            @click:row="expandRow">
            <template v-if="isPopup" v-slot:item="{ item, headers }">
              <v-divider />
              <tr>
                <td :colspan="headers.length" @click="expandRow(item)">
                  <hr style="height:10px; visibility:hidden;" />
                  <template v-for="(header, headerIndex) in headers">
                    <template v-if="header.value !== 'data-table-expand'">
                      <span class="ps-2" style="float:left" :key="header.text + headerIndex">{{ header.text }}</span>:
                      <span class="pe-2" style="float:right" :key="item[header.value] + headerIndex">{{ item[header.value] }}</span>
                      <hr style="height:10px; visibility:hidden;" :key="headerIndex" />
                    </template>
                  </template>
                </td>
              </tr>
            </template>
            <!-- The expanasion area -->
            <template v-if="localContent.expandable" v-slot:expanded-item="{ headers, item }">
              <td style="background-color: #78909C" :colspan="headers.length">
                <contentEngine
                  :contentTitle="item.name"
                  headerType="subtitle"
                  isPopup
                  :contentTagging="contentTagging"
                  :editMode="editMode"
                  :ignoredWords="ignoredWords"
                  :sections="sections"
                  :examples="examples"
                  :key="engineKey"
                  @updateSection="updateSection"
                  @updateExample="updateExample" />
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </template>
    <!-- Text View -->
    <v-row v-else justify="center">
      <v-col cols="11" :lg="isPopup ? 11 : 10" :xl="isPopup ? 11 : 9">
        <!-- Not Edit Mode -->
        <div v-if="!editToggle" :class="leftAlign ? '' : 'text-center'">
          <v-icon
            v-if="(Object.keys(examples).includes(localTitle) || editMode) && !isExample"
            small
            style="float:left"
            color="white"
            @click="chosenWord = localTitle; localIsExample = true; dialogKey++; showTip = true">
            mdi-information-outline
          </v-icon>
          <h1 v-if="headerType === 'title'" class="text-center" v-html="cleanTitle" />
          <h3 v-if="headerType === 'subtitle'" class="text-center" v-html="cleanTitle" />
          <br v-if="headerType !== 'label'" />
          <b v-if="headerType === 'label'" v-html="cleanTitle + ': '" />
          <template v-for="(line, lineIndex) in localContent.split('\n')">
            <!-- Text Tagging -->
            <span v-if="contentTagging" :key="lineIndex">
              <template v-for="(word, wordIndex) in line.split(' ')">
                <span
                  v-if="!tagWordIndexes.includes(lineIndex.toString().concat('-', wordIndex))"
                  v-html="word.replace(/[|]/g, ' ')"
                  :style="isExample ? 'font-style: italic;' : ''"
                  :key="wordIndex" />
                <template v-else>
                  <v-chip small label @click="chosenWord = word; localIsExample = false; dialogKey++; showTip = true" :key="wordIndex">
                    {{ word.replace(/[|]/g, ' ') }}
                  </v-chip>
                </template>
                {{ ' ' }}
              </template>
            </span>
            <!-- No tagging -->
            <span v-else v-html="line.replace(/[|]/g, ' ')" :style="isExample ? 'font-style: italic;' : ''" :key="lineIndex" />
            <br :key="(lineIndex + 1) * -1" />
          </template>
        </div>
        <!-- Edit Mode -->
        <template v-else>
          <v-textarea v-model="localContent" dark :label="localTitle" auto-grow />
          <v-btn v-if="isExample" color="green" class="white--text" @click="updateExample({ section: localTitle, newContent: localContent }); editToggle = !editToggle;" >
            <v-icon color="white">mdi-content-save</v-icon>&nbsp;Save Example
          </v-btn>
          <v-btn v-else color="green" class="white--text" @click="updateSection({ section: localTitle, newContent: localContent }); editToggle = !editToggle;" >
            <v-icon color="white">mdi-content-save</v-icon> &nbsp;Save Content
          </v-btn>
        </template>
      </v-col>
      <!-- Edit Button -->
      <v-col v-if="editMode && !editLock" cols="1">
        <v-btn dark icon @click="editToggle = !editToggle">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <!-- Popup -->
    <v-dialog v-model="showTip" :max-width="popupSize">
      <v-sheet color="primary" :max-width="popupSize - 4" style="overflow: auto;" max-height="750" class="pa-2">
        <v-sheet color="secondary" class="ma-2 white--text">
          <contentEngine
            :contentTitle="chosenWord"
            :ignoredWords="localIgnored"
            isPopup
            :contentTagging="contentTagging"
            :editMode="editMode"
            :isExample="localIsExample"
            :sections="sections"
            :examples="examples"
            :key="dialogKey"
            @updateSection="updateSection"
            @updateExample="updateExample" />
        </v-sheet>
      </v-sheet>
    </v-dialog>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  components: {
    contentEngine: () => import('./contentEngine'),
    test: () => import('@/components/ACTS/viewTraits')
  },
  props: {
    contentTitle: {
      type: String,
      required: true
    },
    content: {
      type: String,
      required: false,
      default: ''
    },
    headerType: {
      type: String,
      required: false,
      default: 'title'
    },
    ignoredWords: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    isPopup: {
      type: Boolean,
      required: false,
      default: false
    },
    docView: {
      type: Boolean,
      required: false,
      default: false
    },
    isExample: {
      type: Boolean,
      required: false,
      default: false
    },
    leftAlign: {
      type: Boolean,
      required: false,
      default: false
    },
    editMode: {
      type: Boolean,
      required: true
    },
    contentTagging: {
      type: Boolean,
      required: true
    },
    sections: {
      type: Object,
      required: true
    },
    examples: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showTip: false,
      dialogKey: 0,
      chosenWord: '',
      editToggle: false,
      editLock: false,
      tagWordIndexes: [],
      repeatTags: [],
      localIgnored: [],
      localContent: '',
      localTitle: '',
      tableView: false,
      componentView: false,
      expandedTableItem: [],
      engineKey: 0,
      localIsExample: false,
      windowWidth: window.innerWidth
    };
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
    });
  },
  created() {
    if (this.sections[this.contentTitle] && this.sections[this.contentTitle].component) {
      this.componentView = true;
    } else {
      this.localTitle = this.contentTitle.replace(/[,.]/g, '').toLowerCase();
      this.localIgnored = _.cloneDeep(this.ignoredWords);
      this.localIgnored.push(this.localTitle);
      if (this.isExample) {
        this.localContent = this.examples[this.localTitle] ? this.examples[this.localTitle] : '';
      } else {
        if (this.localTitle.startsWith('table|')) {
          this.tableView = true;
        }
        if (this.content === '') {
          this.localContent = this.sections[this.localTitle] ? this.sections[this.localTitle] : '';
        } else {
          this.editLock = true;
          this.localContent = this.content;
        }
      }

      if (!this.tableView && this.contentTagging) {
        this.lineSort();
      }

      if (this.tableView && this.docView) {
        this.expandedTableItem = this.localContent.items;
      }
      if (this.localContent.headers) {
        const headerSize = Math.floor(100 / this.localContent.headers.length).toString() + '%';
        this.localContent.headers = this.localContent.headers.map(header => ({ ...header, width: headerSize }));
        console.log(this.localContent.headers);
      }
    }
  },
  methods: {
    lineSort() {
      const lines = this.localContent.split('\n');
      lines.forEach((line, lineIndex) => {
        const words = line.split(' ');
        words.forEach((word, wordIndex) => {
          if (this.checkWord(word)) {
            this.tagWordIndexes.push(lineIndex.toString().concat('-', wordIndex));
          }
        });
      });
    },
    updateSection(content) {
      this.$emit('updateSection', content);
    },
    updateExample(content) {
      this.$emit('updateExample', content);
    },
    checkWord(word) {
      const cleanWord = word.replace(/[,.]/g, '').toLowerCase();
      if (Object.keys(this.sections).includes(cleanWord)) {
        if (!this.localIgnored.includes(cleanWord)) {
          if (!this.repeatTags.includes(cleanWord)) {
            this.repeatTags.push(cleanWord);
            return true;
          }
        }
      }
      return false;
    },
    expandRow(row) {
      if(this.localContent.expandable && !this.docView) {
        if (this.expandedTableItem.length > 0) {
          if (this.expandedTableItem[0].name != row.name) {
            this.expandedTableItem = [row];
          } else {
            this.expandedTableItem = [];
          }
        } else {
          this.expandedTableItem = [row];
        }
        this.engineKey++;
      }
    }
  },
  computed: {
    getHeight() {
      // Each header displayed in a popup is 22 pixels high and has it's own 10 pixel 'gap' above it. 
      // Each row has a single 10 pixel gap at the bottom and a 1 pixel divider. And the TD adds one?
      let size = this.localContent.items.length * ((this.localContent.headers.length * 32) + 12);
      if (size > 600) {
        size = 600;
      }
      return size;
    },
    cleanTitle() {
      return this.localTitle.toUpperCase().replace(/[|]/g, ' ');
    },
    popupSize() {
      let mySize = this.windowWidth / 2;
      if (mySize < 380) {
        mySize = 380;
      }
      return mySize;
    },
    localComponent() {
      if (this.componentView) {
        return () => import('@/components/ceComponents/'.concat(this.sections[this.contentTitle].componentLocation));
      }
      return null;
    }
  }
};
</script>

<style>
.secondary {
  background-color: #607D8B
}
</style>
